import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ElementRef } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Guid } from 'guid-typescript';
import { Observable, Subscription } from 'rxjs';
import { filter, finalize } from 'rxjs/operators';
import { MAX_CUSTOM_ENTRY_DESCRIPTION_LENGTH, CsBuildupStateQuery, CsBuildupStateStore, EditableSectionPartComponent, TotalCostsService } from '../../shared';
import { ChargingStation, CsSetupInfo, CsSetupSection } from '../charging-stations-section.model';
import { ConfirmEmuDeletionDialogComponent, EmuDeletionReason } from '../confirm-emu-deletion-dialog/confirm-emu-deletion-dialog.component';
import { CsSetupSectionsService } from '../services/cs-setup-sections.service';
import { EmuEntryQuery } from '../state/emu-entry.state';
import { EditableCsSetupSection } from './editable-cs-setup-section';

@Component({
  selector: 'app-cs-setup',
  templateUrl: './cs-setup.component.html',
  styleUrls: ['./cs-setup.component.scss']
})
export class CsSetupComponent extends EditableSectionPartComponent implements OnInit, OnDestroy {
  @Input() chargingStations: ChargingStation[];
  @Input() csSetupInfo: CsSetupInfo;
  @Output() removeCsSetupSectionRequest = new EventEmitter<Guid>();
  @Output() updatedCsSetupSection = new EventEmitter<CsSetupSection>();
  @Output() markEmuForDeletion = new EventEmitter<Guid>();
  @Output() unmarkEmuForDeletion = new EventEmitter<Guid>();
  readonly MAX_CUSTOM_ENTRY_DESCRIPTION_LENGTH = MAX_CUSTOM_ENTRY_DESCRIPTION_LENGTH;
  editableCsSetupSection: EditableCsSetupSection;
  displayedColumns: string[] = ['productId', 'quantity', 'discountFactor', 'description', 'pricePerUnit', 'ap', 'totalCost', 'delete'];
  dragDisabled: boolean = true;
  private totalCostsSubscription: Subscription;
  private displayProductUpdatesSubscription: Subscription;

  constructor(
    private totalCostsService: TotalCostsService,
    private emuEntryInfoQuery: EmuEntryQuery,
    private csSetupSectionsService: CsSetupSectionsService,
    private dialog: MatDialog,
    protected element: ElementRef<Element>,
    protected csBuildupStore: CsBuildupStateStore,
    protected csBuildupStateQuery: CsBuildupStateQuery) {
    super(element, csBuildupStore, csBuildupStateQuery);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.editableCsSetupSection = EditableCsSetupSection.createFromCsSetupSection(this.csSetupInfo.csSetupSection, this.chargingStations);
    this.displayProductUpdatesSubscription = this.editableCsSetupSection.displayProductUpdates$
      .subscribe(displayProductUpdates =>
        {
          if (displayProductUpdates) {
            // It is needless to check if the charging station was deleted and to exclude it because in that case
            // the charging station has no related EMUs and the result of the isCurrentEmuInvalid method is the same
            // with or without excluding the charging station.
            if (this.emuEntryInfoQuery.isCurrentEmuInvalid()) {
              this.markEmuForDeletion.emit(this.csSetupInfo.csSetupSection.id);
            }
          }
          else {
            this.unmarkEmuForDeletion.emit(this.csSetupInfo.csSetupSection.id);
          }
        });
    this.totalCostsSubscription = this.editableCsSetupSection.totalCosts$
      .subscribe(totalCosts => this.totalCostsService.addOrUpdatePartialCosts(this.csSetupInfo.csSetupSection.id.toString(), totalCosts));
    if (this.csSetupInfo.isNew) {
      super.startEdit();
    }
  }

  ngOnDestroy(): void {
    this.displayProductUpdatesSubscription.unsubscribe();
    this.totalCostsSubscription.unsubscribe();
    this.totalCostsService.removePartialCosts(this.csSetupInfo.csSetupSection.id.toString());
    super.ngOnDestroy();
  }

  saveChanges(checkIfCurrentEmuGetsInvalid: boolean = true): void {
    const csSetupSection = this.editableCsSetupSection.getCsSetupSection();

    // Add new section
    if (this.csSetupInfo.isNew) {
      this.addOrUpdateCsSetupSection(csSetupSection);
      return;
    }

    // Update existing section
    // Including the charging station for the isCurrentEmuInvalid check is important to ensure that a new selected
    // charging station is considered for the check.
    if (checkIfCurrentEmuGetsInvalid && this.isCurrentEmuInvalid(csSetupSection.chargingStationEntry.productId)) {
      this.openConfirmEmuDeletionDialog(EmuDeletionReason.ChargingStationChanged)
        .afterClosed()
        .pipe(filter(emuDeletionConfirmed => emuDeletionConfirmed))
        .subscribe(() => this.addOrUpdateCsSetupSection(csSetupSection));
    }
    else {
      this.addOrUpdateCsSetupSection(csSetupSection);
    }
  }

  deleteCsSetup(causedByProductUpdate: boolean = false): void {
    const removeCsSetup = (projectId: Guid, csSetupSectionId: Guid) => {
      this.csSetupSectionsService.remove(projectId, csSetupSectionId)
            .subscribe(csSetupSection => this.removeCsSetupSectionRequest.emit(csSetupSection.id));};

    if (this.isCurrentEmuInvalid()) {
      this
        .openConfirmEmuDeletionDialog(
          causedByProductUpdate ? EmuDeletionReason.AlignedToProductUpdates : EmuDeletionReason.ChargingStationDeleted)
        .afterClosed()
        .pipe(filter(emuDeletionConfirmed => emuDeletionConfirmed))
        .subscribe(
          () => removeCsSetup(this.csSetupInfo.projectId, this.csSetupInfo.csSetupSection.id));
    } else {
      removeCsSetup(this.csSetupInfo.projectId, this.csSetupInfo.csSetupSection.id);
    }
  }

  discardChanges(): void {
    if (this.csSetupInfo.isNew) {
      this.removeCsSetupSectionRequest.emit(this.csSetupInfo.csSetupSection.id);
    } else {
      this.editableCsSetupSection.reset();
    }
    super.endEdit();
  }

  alignEntriesToCurrentProducts(): void {
    const alignEntriesToCurrentProductsAndSaveChanges = () => {
      this.editableCsSetupSection.alignEntriesToCurrentProducts();
      this.saveChanges(false);
    };

    if (this.emuEntryInfoQuery.isCurrentEmuInvalid()) {
      this.openConfirmEmuDeletionDialog(EmuDeletionReason.AlignedToProductUpdates)
        .afterClosed()
        .pipe(filter(emuDeletionConfirmed => emuDeletionConfirmed))
        .subscribe(() => alignEntriesToCurrentProductsAndSaveChanges());
    }
    else {
      alignEntriesToCurrentProductsAndSaveChanges();
    }
  }

  private openConfirmEmuDeletionDialog(emuDeletionReason: EmuDeletionReason): MatDialogRef<ConfirmEmuDeletionDialogComponent, boolean> {
    const csEntry = this.csSetupInfo.csSetupSection.chargingStationEntry;
    const emuEntry = this.emuEntryInfoQuery.getValue().emuEntry;
    return this.dialog.open(
      ConfirmEmuDeletionDialogComponent,
      { data: { csEntry: {...csEntry}, emuEntry: {...emuEntry}, emuDeletionReason } });
  }

  private isCurrentEmuInvalid(includeChargingStationId?: number): boolean {
    return this.emuEntryInfoQuery.isCurrentEmuInvalid(
      this.csSetupInfo.csSetupSection.chargingStationEntry.productId,
      includeChargingStationId);
  }

  private addOrUpdateCsSetupSection(csSetupSection: CsSetupSection): void {
    const onAddedOrUpdatedCsSetupSection = (addedOrUpdatedSection$: Observable<CsSetupSection>): void => {
      addedOrUpdatedSection$
        .pipe(finalize(() => super.endEdit()))
        .subscribe((addedSection) => {
          this.updatedCsSetupSection.emit(addedSection);
          this.editableCsSetupSection.applyChanges();
        });
    };

    // Add new section
    if (this.csSetupInfo.isNew) {
      const csSetupSection$ = this.csSetupSectionsService.add(this.csSetupInfo.projectId, csSetupSection);
      onAddedOrUpdatedCsSetupSection(csSetupSection$);
    }
    // Update existing section
    else {
      const csSetupSection$ = this.csSetupSectionsService.update(this.csSetupInfo.projectId, csSetupSection);
      onAddedOrUpdatedCsSetupSection(csSetupSection$);
    }
  }
}



